import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import {
  Joyride,
  STATUS,
  EVENTS,
  ACTIONS,
  type EventData,
  type Controls,
} from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { tourRegistry } from '@/lib/tours/registry';
import { TourTooltip } from '@/components/tours/TourTooltip';
import { useTourCompletions } from '@/hooks/useTourCompletions';
import { useTeamContext } from '@/contexts/TeamContext';
import { useToast } from '@/hooks/use-toast';
import type { TourDefinition } from '@/lib/tours/types';

interface TourContextValue {
  startTour: (guideTitle: string) => void;
  endTour: () => void;
  activeTourId: string | null;
  isTourActive: boolean;
  hasTour: (guideTitle: string) => boolean;
}

const TourContext = createContext<TourContextValue>({
  startTour: () => {},
  endTour: () => {},
  activeTourId: null,
  isTourActive: false,
  hasTour: () => false,
});

export function useTour() {
  return useContext(TourContext);
}

export function TourProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTour, setActiveTour] = useState<TourDefinition | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [run, setRun] = useState(false);
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { markComplete } = useTourCompletions();
  const { activeTeam } = useTeamContext();
  const teamRole = activeTeam?.role ?? null;
  const { toast } = useToast();

  const hasTour = useCallback((guideTitle: string) => {
    const tour = tourRegistry.get(guideTitle);
    if (!tour) return false;
    if (tour.requiredRoles && (!teamRole || !tour.requiredRoles.includes(teamRole as any))) {
      return false;
    }
    return true;
  }, [teamRole]);

  const endTour = useCallback(() => {
    setRun(false);
    setActiveTour(null);
    setStepIndex(0);
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
  }, []);

  const startTour = useCallback(
    (guideTitle: string) => {
      const tour = tourRegistry.get(guideTitle);
      if (!tour) return;
      if (tour.requiredRoles && (!teamRole || !tour.requiredRoles.includes(teamRole as any))) return;

      setActiveTour(tour);
      setStepIndex(0);

      const needsNavigation =
        location.pathname !== tour.startRoute.split('?')[0];
      if (needsNavigation) {
        navigate(tour.startRoute);
      }

      navigationTimeoutRef.current = setTimeout(
        () => setRun(true),
        needsNavigation ? 600 : 300
      );
    },
    [navigate, location.pathname]
  );

  // Global Escape key listener — works even when Joyride's tooltip isn't visible
  useEffect(() => {
    if (!run) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        endTour();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [run, endTour]);

  const completeTour = useCallback(() => {
    if (!activeTour) return;
    markComplete(activeTour.id);
    toast({
      title: 'Tour completed!',
      description: 'Nice work — you can revisit this guide anytime.',
    });
    endTour();
  }, [activeTour, markComplete, endTour, toast]);

  const advanceStep = useCallback(
    (currentIndex: number, direction: 'next' | 'prev' = 'next') => {
      if (!activeTour) return;

      const nextIndex =
        direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

      if (nextIndex < 0 || nextIndex >= activeTour.steps.length) {
        if (direction === 'next') {
          completeTour();
        } else {
          endTour();
        }
        return;
      }

      const nextStep = activeTour.steps[nextIndex];
      const currentRoute =
        activeTour.steps[currentIndex]?.route || activeTour.startRoute;
      const nextRoute = nextStep.route || currentRoute;

      const currentPath = currentRoute.split('?')[0];
      const nextPath = nextRoute.split('?')[0];

      if (nextPath !== currentPath) {
        setRun(false);
        navigate(nextRoute);
        navigationTimeoutRef.current = setTimeout(() => {
          setStepIndex(nextIndex);
          setRun(true);
        }, 600);
      } else {
        setStepIndex(nextIndex);
      }
    },
    [activeTour, navigate, endTour, completeTour]
  );

  const handleEvent = useCallback(
    (data: EventData, _controls: Controls) => {
      const { action, index, status, type } = data;

      if (
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED ||
        action === ACTIONS.CLOSE
      ) {
        if (status === STATUS.FINISHED) {
          completeTour();
        } else {
          endTour();
        }
        return;
      }

      // Auto-skip steps whose target doesn't exist
      if (type === EVENTS.TARGET_NOT_FOUND) {
        advanceStep(index, 'next');
        return;
      }

      if (type === EVENTS.STEP_AFTER) {
        const direction = action === ACTIONS.PREV ? 'prev' : 'next';
        advanceStep(index, direction);
      }
    },
    [endTour, completeTour, advanceStep]
  );

  const joyrideSteps = activeTour
    ? activeTour.steps.map((s) => ({
        target: s.target,
        content: s.content,
        title: s.title,
        placement: (s.placement || 'bottom') as any,
        skipBeacon: s.disableBeacon !== false,
        blockTargetInteraction: !(s.spotlightClicks || false),
        spotlightPadding:
          s.spotlightPadding != null
            ? {
                top: s.spotlightPadding,
                right: s.spotlightPadding,
                bottom: s.spotlightPadding,
                left: s.spotlightPadding,
              }
            : undefined,
        showProgress: true,
        buttons: ['skip', 'back', 'primary'] as any[],
        overlayClickAction: false as const,
        data: {
          isActionStep: s.isActionStep ?? false,
          nextButtonText: s.nextButtonText,
        },
      }))
    : [];

  return (
    <TourContext.Provider
      value={{
        startTour,
        endTour,
        activeTourId: activeTour?.id ?? null,
        isTourActive: run,
        hasTour,
      }}
    >
      {children}

      {/* Escape hint banner */}
      {run && (
        <div className="fixed top-0 left-0 right-0 z-[10002] flex justify-center pointer-events-none">
          <div className="mt-2 px-3 py-1.5 bg-popover/95 border rounded-full shadow-lg text-xs text-muted-foreground pointer-events-auto">
            Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono font-medium">Esc</kbd> to exit tour
          </div>
        </div>
      )}

      <Joyride
        steps={joyrideSteps}
        run={run}
        stepIndex={stepIndex}
        continuous
        scrollToFirstStep
        tooltipComponent={TourTooltip}
        onEvent={handleEvent}
        options={{
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
        }}
      />
    </TourContext.Provider>
  );
}
